import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { useTenant } from "@/lib/tenant";
import { api } from "@/lib/api";
import { PageHeader, Panel } from "@/components/Bits";
import SlackPanel from "@/components/SlackPanel";
import CustomerManagement, { PortalUsers } from "@/components/CustomerManagement";
import DigestPanel from "@/components/DigestPanel";
import MfaPanel from "@/components/MfaPanel";
import WebsiteEditor from "@/components/WebsiteEditor";
import TeamPanel from "@/components/TeamPanel";
import ReferralPanel from "@/components/ReferralPanel";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Download, Copy, Biohazard, Terminal, MonitorCog } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const { tenants, current, tenantId } = useTenant();
  const qc = useQueryClient();

  const policy = useQuery({
    queryKey: ["policy", tenantId],
    queryFn: () => api.get(`/policy/${tenantId}`).then((r) => r.data),
    enabled: !!tenantId,
  });

  const savePolicy = useMutation({
    mutationFn: (p) => api.put(`/policy/${tenantId}`, p).then((r) => r.data),
    onSuccess: (d) => { qc.setQueryData(["policy", tenantId], d); toast.success(d.l3 ? "L3 Autonomous response ENABLED" : "Response policy saved"); },
    onError: () => toast.error("Could not save policy"),
  });

  const models = useQuery({ queryKey: ["ai-models"], queryFn: () => api.get("/ai/models").then((r) => r.data) });

  const [simBusy, setSimBusy] = useState(false);
  const simulate = async () => {
    setSimBusy(true);
    try {
      const { data } = await api.post(`/simulate/ransomware/${tenantId}`);
      toast.warning(`Ransomware burst injected on ${data.endpoint.hostname} · confidence ${Math.round((data.confidence || 0) * 100)}%`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Simulation failed"); }
    finally { setSimBusy(false); }
  };

  const p = policy.data || { l1: true, l2: true, l3: false };
  const toggle = (k) => (v) => savePolicy.mutate({ ...p, [k]: v });
  const isAdmin = user?.role === "admin";
  const isCustomer = user?.role === "customer";

  if (isCustomer) {
    return (
      <>
        <PageHeader title="Settings" kicker="Customer Portal · Account" />
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Panel kicker="Signed in as" title={user?.name}>
            <div className="p-4 space-y-2 text-sm">
              <div className="flex justify-between border-b hairline pb-2"><span className="text-zinc-500">Email</span><span className="font-mono text-xs" data-testid="settings-email">{user?.email}</span></div>
              <div className="flex justify-between border-b hairline pb-2"><span className="text-zinc-500">Access</span><span className="font-mono text-xs uppercase text-cyan-300">{user?.demo ? "demo · read-only" : user?.team_role || "viewer"}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Organisation</span><span>{current?.name || "—"}</span></div>
            </div>
          </Panel>
          {!user?.demo && <ChangePasswordPanel />}
          {!user?.demo && <TeamPanel />}
          {!user?.demo && <ReferralPanel />}
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Settings" kicker="Account · Response Policy · Live Agent · Customers" />
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Panel kicker="Signed in as" title={user?.name}>
          <div className="p-4 space-y-2 text-sm">
            <div className="flex justify-between border-b hairline pb-2"><span className="text-zinc-500">Email</span><span className="font-mono text-xs" data-testid="settings-email">{user?.email}</span></div>
            <div className="flex justify-between border-b hairline pb-2"><span className="text-zinc-500">Role</span><span className="font-mono text-xs uppercase text-cyan-300">{user?.role}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Active tenant</span><span>{current?.name || "—"}</span></div>
          </div>
        </Panel>

        <Panel kicker={`AI Response Policy · ${current?.name || ""}`} title="Autonomy Level">
          <div className="p-4 space-y-3 text-sm">
            <ToggleRow testid="policy-l1" label="Level 1 · Observe" desc="AI can analyze, correlate and recommend only. No changes." checked={p.l1} onChange={toggle("l1")} />
            <ToggleRow testid="policy-l2" label="Level 2 · Assisted Response" desc="AI proposes actions; a human must approve." checked={p.l2} onChange={toggle("l2")} />
            <ToggleRow testid="policy-l3" label="Level 3 · Autonomous (ransomware > 95% confidence)" desc="Automatically isolate endpoint, kill process, preserve evidence. The SOC is toasted in real time." checked={p.l3} onChange={toggle("l3")} danger />
            <button data-testid="simulate-ransomware-btn" onClick={simulate} disabled={simBusy || !tenantId}
              className="w-full border border-orange-400/50 text-orange-400 hover:bg-orange-400/10 px-3 py-2 text-[11px] font-mono uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50 transition-colors duration-150">
              <Biohazard size={13} /> {simBusy ? "INJECTING…" : "SIMULATE RANSOMWARE BURST ON RANDOM ENDPOINT"}
            </button>
          </div>
        </Panel>

        <AgentPanel tenant={current} />

        <SlackPanel tenant={current} />

        {isAdmin && <CustomerManagement tenant={current} />}
        {isAdmin && <PortalUsers tenant={current} />}
        <DigestPanel tenant={current} />
        <MfaPanel />
        <ChangePasswordPanel />
        {isAdmin && <WebsiteEditor />}

        <Panel kicker="AI SOC Analyst" title="Model availability">
          <div className="divide-y divide-white/5 text-sm">
            {(models.data || []).map((m) => (
              <div key={m.id} className="px-4 py-3 flex items-center justify-between" data-testid={`model-row-${m.id}`}>
                <div>
                  <div className="font-semibold">{m.label} {m.default && <span className="ml-2 text-[9px] font-mono uppercase text-cyan-300">default</span>}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{m.vendor} · {m.provider === "perplexity" ? "PERPLEXITY_API_KEY" : "Emergent Universal Key"}</div>
                </div>
                <span className={`font-mono text-[10px] uppercase tracking-widest ${m.available ? "text-emerald-400" : "text-orange-400"}`}>{m.available ? "ready" : "key required"}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel kicker="MSP Tenants" title={`${tenants.length} customers`}>
          <div className="divide-y divide-white/5 text-sm">
            {tenants.map((t) => (
              <div key={t.id} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{t.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{t.industry} · {t.endpoint_count} endpoints</div>
                </div>
                <span className="font-mono text-[10px] text-zinc-500">{t.slug}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </>
  );
}

const AgentPanel = ({ tenant }) => {
  const server = window.location.origin;
  const key = tenant?.enrollment_key || "<key>";
  const cmd = `python cdscyber_agent.py --server ${server} --key ${key}`;
  const winCmd = `irm "${server}/api/agent/install.ps1?key=${key}&server=${encodeURIComponent(server)}" | iex`;
  const copy = (txt, what) => navigator.clipboard.writeText(txt).then(() => toast.success(`${what} copied`));
  const download = async (ps) => {
    const { data } = await api.get(ps ? "/agent/script.ps1" : "/agent/script", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a"); a.href = url; a.download = ps ? "cdscyber_agent.ps1" : "cdscyber_agent.py"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Panel kicker={`Live Agent · ${tenant?.name || ""}`} title="Enroll a real machine">
      <div className="p-4 space-y-3 text-sm">
        <p className="text-xs text-zinc-400">A lightweight agent posts live host, process and posture telemetry into this tenant every 10 seconds. On Windows it also ships new <span className="text-zinc-200">Security</span> (4624/4625/4688/4720/4732/1102), <span className="text-zinc-200">System</span> (7045) and <span className="text-zinc-200">Sysmon</span> (1/3/11/13) events with MITRE mapping — the console correlates them into incidents. Add <span className="font-mono text-zinc-300">-SimulateRansomware</span> to exercise the L3 auto-isolate engine.</p>
        <div className="border hairline p-3">
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Enrollment key</div>
          <div className="flex items-center gap-2">
            <code data-testid="enrollment-key" className="font-mono text-xs text-cyan-300 flex-1 truncate">{tenant?.enrollment_key || "—"}</code>
            <button data-testid="copy-key-btn" onClick={() => copy(tenant?.enrollment_key || "", "Enrollment key")} className="text-zinc-400 hover:text-white"><Copy size={13} /></button>
          </div>
        </div>
        <div className="border border-cyan-400/30 bg-cyan-400/[0.03] p-3">
          <div className="text-[9px] font-mono uppercase tracking-widest text-cyan-300 mb-1 flex items-center gap-1"><MonitorCog size={10} /> Windows · one-line installer (Admin PowerShell) · no Python, nothing else to install</div>
          <div className="flex items-start gap-2">
            <code data-testid="windows-installer-command" className="font-mono text-[11px] text-zinc-100 break-all flex-1">{winCmd}</code>
            <button data-testid="copy-installer-btn" onClick={() => copy(winCmd, "Installer command")} className="text-zinc-400 hover:text-white"><Copy size={13} /></button>
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">Pure PowerShell agent (built into every Windows). Drops into %ProgramData%\CyboScan and registers a hidden SYSTEM scheduled task that starts at boot and auto-restarts. Uninstall: <span className="font-mono">$env:CDS_UNINSTALL=1</span> then re-run the same line.</div>
        </div>
        <div className="border hairline p-3 bg-black/40">
          <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1"><Terminal size={10} /> Linux / macOS · Python (optional)</div>
          <div className="flex items-start gap-2">
            <code data-testid="agent-command" className="font-mono text-[11px] text-zinc-200 break-all flex-1">{cmd}</code>
            <button data-testid="copy-cmd-btn" onClick={() => copy(cmd, "Command")} className="text-zinc-400 hover:text-white"><Copy size={13} /></button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="download-agent-ps1-btn" onClick={() => download(true)}
            className="border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 px-3 py-2 text-[11px] font-mono uppercase tracking-widest flex items-center justify-center gap-2 transition-colors duration-150">
            <Download size={13} /> cdscyber_agent.ps1
          </button>
          <button data-testid="download-agent-btn" onClick={() => download(false)}
            className="border hairline text-zinc-300 hover:bg-white/5 px-3 py-2 text-[11px] font-mono uppercase tracking-widest flex items-center justify-center gap-2 transition-colors duration-150">
            <Download size={13} /> cdscyber_agent.py
          </button>
        </div>
      </div>
    </Panel>
  );
};

const ToggleRow = ({ label, desc, checked, onChange, testid, danger }) => (
  <label className={`flex items-start gap-3 border p-3 cursor-pointer ${danger && checked ? "border-red-500/50 bg-red-950/20" : "hairline"}`}>
    <Switch data-testid={testid} checked={!!checked} onCheckedChange={onChange} className="mt-1" />
    <div>
      <div className="text-sm font-medium">{label}</div>
      <div className="text-[11px] text-zinc-500">{desc}</div>
    </div>
  </label>
);

const ChangePasswordPanel = () => {
  const [f, setF] = useState({ current: "", next: "" });
  const [busy, setBusy] = useState(false);
  const cls = "w-full bg-[#1B1B28] border hairline px-3 py-2 text-sm focus:outline-none focus:border-cyan-400/60";
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/change-password", { current_password: f.current, new_password: f.next }); toast.success("Password changed"); setF({ current: "", next: "" }); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not change password"); }
    finally { setBusy(false); }
  };
  return (
    <Panel kicker="Security" title="Change password">
      <form onSubmit={submit} className="p-4 space-y-3 text-sm">
        <input data-testid="cp-current" type="password" required className={cls} placeholder="Current password" value={f.current} onChange={(e) => setF({ ...f, current: e.target.value })} />
        <input data-testid="cp-new" type="password" required minLength={10} className={cls} placeholder="New password (min 10 chars)" value={f.next} onChange={(e) => setF({ ...f, next: e.target.value })} />
        <button data-testid="cp-submit" disabled={busy} className="w-full border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 px-3 py-2 text-[11px] font-mono uppercase tracking-widest disabled:opacity-50">Update password</button>
      </form>
    </Panel>
  );
};
